// docs/66 RM10, RM11: what is coming, drawn on the board while the party plans — a called reserve's
// arrival squares outlined on the field's edge with an arrow in and how many come, and every body a
// raising will stand back up ringed with its name. Nothing changes on the board without this
// telegraph first; the HUD's rule strip carries the same lines with their countdown.
import Phaser from 'phaser';
import type { PendingCard, Snapshot, Tile } from '../../core/sim/types';
import { edgeDirOn } from './edges';

export const CALL_COLOUR = 0xff9a3c;
export const RAISE_COLOUR = 0xb48cff;
const KEYLINE = 0x1a1006;

/** How many come for a call card (its group's size), from the reserve. */
export function arrivals(card: PendingCard, snapshot: Pick<Snapshot, 'reserve'>): number | undefined {
  return card.group !== undefined ? snapshot.reserve?.[card.group]?.count : undefined;
}

/** The squares a raise card will stand back up, from the bodies on the board, with names. */
export function raisedBodies(card: PendingCard, snapshot: Pick<Snapshot, 'units' | 'board'>): Array<{ unit: number; tile: Tile; name: string }> {
  return (card.units ?? []).flatMap((id) => {
    const u = snapshot.units.find((x) => x.unitId === id);
    const body = snapshot.board.fallen?.find((f) => f.unit === id);
    const tile = body?.tile ?? u?.tile;
    return tile ? [{ unit: id, tile, name: u?.name ?? `#${id}` }] : [];
  });
}

export class CallLayer {
  private g?: Phaser.GameObjects.Graphics;
  private labels: Phaser.GameObjects.Text[] = [];
  private pulse?: Phaser.Tweens.Tween;

  constructor(private readonly scene: Phaser.Scene, private readonly layer: Phaser.GameObjects.Layer, private readonly cell: number, private readonly dpr: number) {}

  clear(): void {
    this.pulse?.stop(); this.pulse = undefined;
    this.g?.destroy(); this.g = undefined;
    for (const t of this.labels) t.destroy();
    this.labels = [];
  }

  /** Squares drawn now (for the gallery driver and tests). */
  drawn = 0;

  draw(snapshot: Snapshot | undefined): void {
    this.clear();
    this.drawn = 0;
    const cards = (snapshot?.pending ?? []).filter((p) => p.kind === 'Call' || p.kind === 'Raise');
    if (!snapshot || !cards.length) return;
    const c = this.cell;
    const g = this.scene.add.graphics();
    this.g = g;
    this.layer.add(g);
    const label = (t: Tile, text: string, colour: number, below = false): void => {
      const o = this.scene.add.text((t.x + 0.5) * c, below ? (t.y + 1) * c + 1 : t.y * c - 1, text, {
        fontFamily: '"Alegreya Sans", sans-serif', fontStyle: '700', fontSize: `${Math.max(6, Math.round(c * 0.36))}px`,
        color: `#${colour.toString(16).padStart(6, '0')}`, stroke: '#1a1006', strokeThickness: 2,
      }).setOrigin(0.5, below ? 0 : 1).setResolution(this.dpr * 3);
      this.layer.add(o);
      this.labels.push(o);
    };
    for (const card of cards) {
      if (card.kind === 'Call') {
        const tiles = card.tiles;
        for (const t of tiles) {
          const x = t.x * c, y = t.y * c;
          g.fillStyle(CALL_COLOUR, 0.22); g.fillRect(x + 1, y + 1, c - 2, c - 2);
          g.lineStyle(3, KEYLINE, 0.55); g.strokeRect(x + 1.5, y + 1.5, c - 3, c - 3);
          g.lineStyle(1, CALL_COLOUR, 1); g.strokeRect(x + 1.5, y + 1.5, c - 3, c - 3);
          // an arrow pointing in from the edge they come over
          const out = edgeDirOn(snapshot.board, t.x, t.y);
          const [dx, dy] = out === 'w' ? [1, 0] : out === 'e' ? [-1, 0] : out === 'n' ? [0, 1] : [0, -1];
          const cx = x + c / 2, cy = y + c / 2, a = c * 0.28;
          const tip = { x: cx + dx * a, y: cy + dy * a }, tail = { x: cx - dx * a, y: cy - dy * a };
          const side = { x: -dy * a * 0.6, y: dx * a * 0.6 };
          for (const [w, col, al] of [[3, KEYLINE, 0.55], [1, CALL_COLOUR, 1]] as const) {
            g.lineStyle(w, col, al);
            g.lineBetween(tail.x, tail.y, tip.x, tip.y);
            g.lineBetween(tip.x, tip.y, tip.x - dx * a * 0.6 + side.x, tip.y - dy * a * 0.6 + side.y);
            g.lineBetween(tip.x, tip.y, tip.x - dx * a * 0.6 - side.x, tip.y - dy * a * 0.6 - side.y);
          }
          this.drawn++;
        }
        const n = arrivals(card, snapshot);
        const first = [...tiles].sort((p, q) => p.y - q.y || p.x - q.x)[0];
        if (first) label(first, n ? `${n} ARRIVE HERE` : 'ARRIVALS HERE', CALL_COLOUR);
      } else {
        for (const b of raisedBodies(card, snapshot)) {
          const cx = (b.tile.x + 0.5) * c, cy = (b.tile.y + 0.5) * c;
          g.lineStyle(3, KEYLINE, 0.55); g.strokeCircle(cx, cy, c * 0.44);
          g.lineStyle(1, RAISE_COLOUR, 1); g.strokeCircle(cx, cy, c * 0.44);
          g.fillStyle(RAISE_COLOUR, 0.18); g.fillCircle(cx, cy, c * 0.42);
          label(b.tile, `${b.name.toUpperCase()} RISES`, RAISE_COLOUR, true);
          this.drawn++;
        }
      }
    }
    // a slow breath so it reads as something about to happen
    this.pulse = this.scene.tweens.add({ targets: g, alpha: 0.55, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
  }
}
